import { unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n } from '../../i18n.js';
import {
  catalogError,
  cleanText,
  display,
  moveItem,
  storeLocales,
  useStore,
  type Localized,
  type StoreDetails,
} from './shared.js';

type CollectionSummary = Schemas['CollectionSummary'];
type Collection = Schemas['Collection'];
type ProductSummary = Schemas['ProductSummary'];
type Status = Collection['status'];

const SEARCH_DELAY_MS = 300;

/** Store collections. */
export function CollectionsPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatDateTime } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [items, setItems] = useState<CollectionSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (after?: string) => {
      setBusy(true);
      try {
        const page = await unwrap(
          api.GET('/admin-api/v1/stores/{storeId}/collections', {
            params: {
              path: { storeId },
              query: { limit: 50, ...(after === undefined ? {} : { cursor: after }) },
            },
          }),
        );
        setItems((current) =>
          after === undefined ? page.items : [...(current ?? []), ...page.items],
        );
        setCursor(page.nextCursor);
      } catch (failure) {
        setError(errorMessage(t, failure));
        setItems((current) => current ?? []);
      } finally {
        setBusy(false);
      }
    },
    [storeId, t],
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null || items === null) return <Loading />;
  const canManage = store.permissions.includes('catalog.manage');

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <div className="admin-title-row">
        <h1 className="mrigl-heading">{t('admin.catalog.collections')}</h1>
        {canManage ? (
          <Link
            className="mrigl-button mrigl-button--primary admin-cta"
            to={`/stores/${storeId}/collections/new`}
          >
            {t('admin.collection.new')}
          </Link>
        ) : null}
      </div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {items.length === 0 ? (
        <p className="mrigl-muted">{t('admin.collection.empty')}</p>
      ) : (
        <ul className="mrigl-list mrigl-card">
          {items.map((item) => (
            <li key={item.collectionId} className="admin-row">
              <Link to={`/stores/${storeId}/collections/${item.collectionId}`}>
                {display(item.title, locale, store, item.handle)}
              </Link>
              <span className="mrigl-muted">
                {t(`admin.collection.status.${item.status}`)} ·{' '}
                {t('admin.collection.productCount', { count: item.productCount })} ·{' '}
                {formatDateTime(item.updatedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {cursor === null ? null : (
        <Button variant="secondary" busy={busy} onClick={() => void load(cursor)}>
          {t('admin.loadMore')}
        </Button>
      )}
    </div>
  );
}

/** Collection create/edit with ordered products. */
export function CollectionEditorPage() {
  const { storeId = '', collectionId } = useParams();
  const { t } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (collectionId === undefined) return;
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/collections/{collectionId}', {
        params: { path: { storeId, collectionId } },
      }),
    )
      .then(setCollection)
      .catch((failure: unknown) => {
        setLoadError(errorMessage(t, failure));
      });
  }, [storeId, collectionId, t]);

  const error = storeError ?? loadError;
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null || (collectionId !== undefined && collection === null)) return <Loading />;
  return (
    <CollectionEditor
      key={collection?.collectionId ?? 'new'}
      store={store}
      collection={collection}
      onSaved={setCollection}
    />
  );
}

function CollectionEditor({
  store,
  collection,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly collection: Collection | null;
  readonly onSaved: (collection: Collection) => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const locales = storeLocales(store);
  const canManage = store.permissions.includes('catalog.manage');
  const [title, setTitle] = useState<Localized>(collection?.title ?? {});
  const [description, setDescription] = useState<Localized>(collection?.description ?? {});
  const [handle, setHandle] = useState(collection?.handle ?? '');
  const [status, setStatus] = useState<Status>(collection?.status ?? 'draft');
  const [products, setProducts] = useState<ProductSummary[]>(collection?.products ?? []);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductSummary[]>([]);
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (term === '') {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      void unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/products', {
          params: {
            path: { storeId: store.storeId },
            query: { q: term, pageSize: 10, sort: 'title' },
          },
        }),
      )
        .then((page) => {
          setResults(page.items);
        })
        .catch(() => {
          setResults([]);
        });
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [query, store.storeId]);

  const name = (product: ProductSummary) => display(product.title, locale, store, product.handle);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    const body = {
      ...(handle.trim() === '' ? {} : { handle: handle.trim().toLowerCase() }),
      status,
      title: cleanText(title),
      description: cleanText(description),
      productIds: products.map((product) => product.productId),
    };
    try {
      if (collection === null) {
        const created = await unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/collections', {
            params: { path: { storeId: store.storeId } },
            body,
          }),
        );
        void navigate(`/stores/${store.storeId}/collections/${created.collectionId}`, {
          replace: true,
          state: { notice: t('admin.collection.saved') },
        });
      } else {
        const saved = await unwrap(
          api.PUT('/admin-api/v1/stores/{storeId}/collections/{collectionId}', {
            params: { path: { storeId: store.storeId, collectionId: collection.collectionId } },
            body: { ...body, version: collection.version },
          }),
        );
        onSaved(saved);
        setProducts(saved.products);
        setHandle(saved.handle);
        setNotice(t('admin.collection.saved'));
      }
    } catch (failure) {
      setError(catalogError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (collection === null) return;
    setBusy(true);
    try {
      await unwrap(
        api.DELETE('/admin-api/v1/stores/{storeId}/collections/{collectionId}', {
          params: { path: { storeId: store.storeId, collectionId: collection.collectionId } },
        }),
      );
      void navigate(`/stores/${store.storeId}/collections`, { replace: true });
    } catch (failure) {
      setError(catalogError(t, failure));
      setBusy(false);
    }
  }

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${store.storeId}/collections`}>{t('admin.catalog.collections')}</Link>
      </p>
      <h1 className="mrigl-heading">
        {collection === null
          ? t('admin.collection.new')
          : display(collection.title, locale, store, collection.handle)}
      </h1>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form
        className="mrigl-stack"
        aria-label={collection === null ? t('admin.collection.new') : t('admin.collection.edit')}
        onSubmit={(event) => void save(event)}
      >
        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.product.details')}</legend>
          {locales.map((code) => (
            <TextField
              key={`title-${code}`}
              lang={code}
              dir="auto"
              maxLength={255}
              required={code === store.defaultLocale}
              label={t('admin.product.titleIn', { language: t(`admin.language.${code}`) })}
              value={title[code] ?? ''}
              onChange={(event) => {
                setTitle({ ...title, [code]: event.target.value });
              }}
            />
          ))}
          {locales.map((code) => (
            <TextAreaField
              key={`description-${code}`}
              lang={code}
              dir="auto"
              maxLength={10_000}
              label={t('admin.product.descriptionIn', { language: t(`admin.language.${code}`) })}
              value={description[code] ?? ''}
              onChange={(event) => {
                setDescription({ ...description, [code]: event.target.value });
              }}
            />
          ))}
          <SelectField
            label={t('admin.product.column.status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as Status);
            }}
            options={(['draft', 'active'] as const).map((value) => ({
              value,
              label: t(`admin.collection.status.${value}`),
            }))}
          />
          <TextField
            ltr
            label={t('admin.product.handle')}
            hint={t('admin.product.handleHint')}
            maxLength={100}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            value={handle}
            onChange={(event) => {
              setHandle(event.target.value);
            }}
          />
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.collection.products')}</legend>
          {products.length === 0 ? (
            <p className="mrigl-muted">{t('admin.collection.noProducts')}</p>
          ) : null}
          <ol className="admin-ordered">
            {products.map((product, index) => (
              <li key={product.productId} className="admin-row">
                <span>{name(product)}</span>
                <span className="admin-actions">
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label={t('admin.product.moveUp', { name: name(product) })}
                    disabled={index === 0}
                    onClick={() => {
                      setProducts(moveItem(products, index, -1));
                    }}
                  >
                    <span aria-hidden="true">↑</span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label={t('admin.product.moveDown', { name: name(product) })}
                    disabled={index === products.length - 1}
                    onClick={() => {
                      setProducts(moveItem(products, index, 1));
                    }}
                  >
                    <span aria-hidden="true">↓</span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setProducts(
                        products.filter((other) => other.productId !== product.productId),
                      );
                    }}
                  >
                    {t('admin.collection.removeProduct', { name: name(product) })}
                  </Button>
                </span>
              </li>
            ))}
          </ol>
          {canManage ? (
            <>
              <TextField
                type="search"
                label={t('admin.collection.findProducts')}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <ul className="mrigl-list">
                {results
                  .filter(
                    (result) => !products.some((product) => product.productId === result.productId),
                  )
                  .map((result) => (
                    <li key={result.productId}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setProducts([...products, result]);
                        }}
                      >
                        {t('admin.collection.addProduct', { name: name(result) })}
                      </Button>
                    </li>
                  ))}
              </ul>
            </>
          ) : null}
        </fieldset>
        {canManage ? (
          <Button type="submit" busy={busy}>
            {t('admin.collection.save')}
          </Button>
        ) : null}
      </form>
      {canManage && collection !== null ? (
        <div className="admin-actions">
          {confirmDelete ? (
            <>
              <Button variant="danger" busy={busy} onClick={() => void remove()}>
                {t('admin.collection.confirmDelete')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmDelete(false);
                }}
              >
                {t('admin.media.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              {t('admin.collection.delete')}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
